/**
 * Runtime 同源代理请求：超时、鉴权头、写操作软门禁。
 * 浏览器始终访问当前 Portal 源，由 server.mjs 转发到 Runtime。
 */
import {
  canWriteBusiness,
  getRuntimeApiKey,
  getRuntimeWhoami,
  promptForRuntimeApiKey,
  updateAuthGateBanner,
} from '../auth.js';

/** 空字符串表示走 Portal 同源代理 */
export const runtimeBaseUrl = '';

export const RUNTIME_DEFAULT_TIMEOUT_MS = 120000;
export const RUNTIME_GENERATE_TIMEOUT_MS = 300000;
/**
 * 长耗时接口：解析/生成/审阅/打分/分析、Workflow/Skill、立项初评（POST projects）、
 * 知识库/Copilot 问答、外部情报查询、审批续跑等。
 */
export const RUNTIME_GENERATE_PATH_RE =
  /\/(generate|parse|review|compare|consistency|supplement-impact|history-compare|score|analyze|ask)(\/|$|\?)|\/workflows\/|\/skills\/|\/resume(\/|$|\?)|\/approvals\/[^/]+\/(approve|reject)(\/|$|\?)|\/external-data\/company(\/|$|\?)|\/api\/business\/projects(\/|$|\?)/i;

export function resolveRuntimeTimeoutMs(path, options = {}) {
  if (typeof options.timeoutMs === 'number' && options.timeoutMs > 0) {
    return options.timeoutMs;
  }
  return RUNTIME_GENERATE_PATH_RE.test(String(path || ''))
    ? RUNTIME_GENERATE_TIMEOUT_MS
    : RUNTIME_DEFAULT_TIMEOUT_MS;
}

/**
 * @param {string} path
 * @param {RequestInit & { timeoutMs?: number }} [options]
 * @param {boolean} [hasRetriedAuth]
 * @param {boolean} [skipWriteGuard] 跳过 viewer 写门禁（如 whoami / health）
 */
export async function requestRuntime(
  path,
  options = {},
  hasRetriedAuth = false,
  skipWriteGuard = false,
) {
  const method = String(options.method || 'GET').toUpperCase();
  const whoami = getRuntimeWhoami();
  if (
    !skipWriteGuard &&
    method !== 'GET' &&
    method !== 'HEAD' &&
    whoami.role &&
    !canWriteBusiness()
  ) {
    throw new Error(
      '当前角色为只读（viewer），无法执行写操作。请更换具备 ops/admin 权限的 API 密钥。',
    );
  }
  const apiKey = getRuntimeApiKey();
  const timeoutMs = resolveRuntimeTimeoutMs(path, options);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const callerSignal = options.signal;
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }
  // 不把 timeoutMs / signal 透传给 fetch 的 rest，避免覆盖本层 AbortController
  const {
    timeoutMs: _ignoredTimeout,
    signal: _ignoredSignal,
    headers: optionHeaders,
    ...fetchOptions
  } = options;
  try {
    const response = await fetch(runtimeBaseUrl + path, {
      ...fetchOptions,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(apiKey ? { 'X-Api-Key': apiKey } : {}),
        ...(optionHeaders || {}),
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (response.status === 401) {
      const config = window.__PE_AI_PORTAL_CONFIG__ || {};
      const statusEl = document.getElementById('runtime-status');
      if (statusEl) statusEl.textContent = 'Runtime 需要 API 密钥';
      updateAuthGateBanner({ unauthorized: true });
      if (!config.proxyInjectedApiKey && !hasRetriedAuth) {
        if (promptForRuntimeApiKey('Runtime 鉴权失败，请重新输入 API 密钥。')) {
          return requestRuntime(path, options, true, skipWriteGuard);
        }
      }
      throw new Error(
        config.proxyInjectedApiKey
          ? 'Runtime 鉴权失败，请确认 Portal 已配置 PE_AI_API_KEY 并刷新页面。'
          : 'Runtime 鉴权失败，请在登录对话框中输入有效的 API 密钥。',
      );
    }
    if (!response.ok) {
      throw new Error(
        payload.message ||
          payload.error ||
          payload.errorCode ||
          'Runtime 请求失败',
      );
    }
    return payload;
  } catch (error) {
    if (error && (error.name === 'AbortError' || error.name === 'TimeoutError')) {
      throw new Error(
        `请求超时（超过 ${Math.round(timeoutMs / 1000)} 秒）。请稍后重试；生成/解析类接口可缩小材料后再试。`,
      );
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}
